import type { Payable } from '@klear/payable/payable'
import { useCallback, useEffect, useRef, useState } from 'react'
import { produce } from 'immer'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { ROUTE_PAYABLE, ROUTE_PAYABLES, buildNavPathTrader } from '@klear/constants'
import { normalizeCurrencyCode } from '@klear/constants/currency'
import { lineItemsFromQuery, lineItemsToQuery } from '@/app/payable/workflow/payable-url-items'
import { STEP_ID, STEP_ORDER, type StepId } from '@klear/payable/constants'
import { PAY_NOW_SENDING_TOAST } from '@klear/payable/list-display'
import { placeholderAccountSides } from '@klear/payable/placeholder-account'
import { EMPTY_PAYABLE_FORM, type PayableForm, type PayableFormDirty } from '@klear/payable/wire.client'
import type { PaymentItemWire } from '@klear/payment/item.wire'
import isError from 'lodash/isError'
import toString from 'lodash/toString'
import { toast } from '@klear/ui/hooks/use-toast'
import { wireValidationErrorMapFromTrpcError } from '@klear/schema/wire/trpc-form-errors'
import {
  commitInputForComplete,
  commitInputForPublish,
  commitInputForSave,
  formValuesToCommitInput,
  payableToFormValues
} from '@/app/payable/workflow/payable-commit-form'
import {
  hasPayableFormChanges,
  markPayableFormDirtyPaths,
  type PayableFormFieldPath
} from '@/app/payable/workflow/payable-form-state'
import { uploadDocument } from '@klear/ui/lib/upload-document'
import { usePayable, usePayableMutations } from '@klear/ui/hooks/use-payables'
import { positiveIntFromQuery, positiveIntsFromQuery, stepIdFromHash } from '@/app/workflow/wizard-url-seed'

const DEFERRED_RECEIPT_FAILURE = 'Saved, but the document upload failed — try it again from the Payment step.'

export type PayableWizardMode = 'create' | 'view' | 'edit'

export function isPayableHostStepSatisfied(stepId: StepId, form: PayableForm): boolean {
  switch (stepId) {
    case STEP_ID.SUPPLIER:
      return form.supplierId != null && form.supplierId > 0
    case STEP_ID.OPERATIONAL:
      return form.operationalEventIds.length > 0 || form.items.length > 0
    case STEP_ID.PAYMENT:
      return form.payment != null && placeholderAccountSides(form.payment).length === 0
    case STEP_ID.REVIEW:
      return false
    default:
      return false
  }
}

export function initialStepForMode(mode: PayableWizardMode, form: PayableForm, hashStep?: StepId | null): StepId {
  if (mode === 'view') return STEP_ID.REVIEW
  if (hashStep != null) {
    const idx = STEP_ORDER.indexOf(hashStep)
    if (idx >= 0) {
      const priorOk = STEP_ORDER.slice(0, idx).every((s) => isPayableHostStepSatisfied(s, form))
      if (priorOk) return hashStep
    }
  }
  if (form.payableId != null) return STEP_ID.PAYMENT
  if (form.operationalEventIds.length > 0 || form.items.length > 0) return STEP_ID.PAYMENT
  if (form.supplierId != null && form.supplierId > 0) return STEP_ID.OPERATIONAL
  return STEP_ID.SUPPLIER
}

export function usePayableWizard(opts: {
  mode: PayableWizardMode
  traderId: number
  traderIdParam: string
  payableId?: number
  orderIdSeed?: number | null
  editable?: boolean
}) {
  const { mode, traderId, traderIdParam, payableId, orderIdSeed, editable = false } = opts
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const readOnly = mode === 'view' && !editable

  const purchaseOrderIdSeed = positiveIntFromQuery(searchParams.get('purchaseOrderId'))
  const supplierIdSeed = positiveIntFromQuery(searchParams.get('supplierId'))
  const currencySeed = normalizeCurrencyCode(searchParams.get('currency'))
  const itemsSeed = lineItemsFromQuery(searchParams.get('items'))
  const operationalIdSeed = positiveIntFromQuery(searchParams.get('operationalId'))
  const operationalIdsSeed = positiveIntsFromQuery(searchParams.get('operationalIds'))
  const seededOperationalIds = [
    ...new Set([...operationalIdsSeed, ...(operationalIdSeed != null ? [operationalIdSeed] : [])])
  ]
  const hashStepSeed = stepIdFromHash(location.hash, STEP_ORDER)

  const { payable, isLoading: isLoadingPayable } = usePayable(payableId ?? 0, {
    enabled: payableId != null && payableId > 0,
    traderId
  })

  const [form, setForm] = useState<PayableForm>(() => ({
    ...EMPTY_PAYABLE_FORM,
    supplierId: supplierIdSeed,
    currency: currencySeed ?? EMPTY_PAYABLE_FORM.currency,
    items: itemsSeed,
    orderId: orderIdSeed ?? null,
    purchaseOrderId: purchaseOrderIdSeed,
    operationalEventIds: seededOperationalIds,
    payableId: payableId ?? null
  }))
  const [dirty, setDirty] = useState<PayableFormDirty>({})
  const [previewResult, setPreviewResult] = useState<Payable | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [pendingReceipts, setPendingReceipts] = useState<File[]>([])
  const previewRequestIdRef = useRef(0)
  const formRef = useRef(form)
  const dirtyRef = useRef(dirty)
  const pendingReceiptsRef = useRef(pendingReceipts)
  formRef.current = form
  dirtyRef.current = dirty
  pendingReceiptsRef.current = pendingReceipts

  const getForm = useCallback(() => formRef.current, [])

  useEffect(() => {
    if (payableId == null || !payable) return
    const nextForm = payableToFormValues(payable)
    formRef.current = nextForm
    dirtyRef.current = {}
    setForm(nextForm)
    setDirty({})
  }, [mode, payable, payableId])

  const { previewPayableCommit, commitPayableCommit, markPaid, isPreviewing, isCommitting } = usePayableMutations()

  const addPendingReceipts = useCallback((files: File[]) => {
    setPendingReceipts((current) => [...current, ...files])
  }, [])

  const removePendingReceipt = useCallback((index: number) => {
    setPendingReceipts((current) => current.filter((_, position) => position !== index))
  }, [])

  const patchForm = useCallback(
    (recipe: (draft: PayableForm) => void, dirtyPaths?: PayableFormFieldPath | readonly PayableFormFieldPath[]) => {
      const next = produce(formRef.current, recipe)
      formRef.current = next
      setForm(next)
      if (dirtyPaths != null) {
        const nextDirty = produce(dirtyRef.current, (draft) => {
          markPayableFormDirtyPaths(draft, dirtyPaths)
        })
        dirtyRef.current = nextDirty
        setDirty(nextDirty)
      }
      setPreviewResult(null)
    },
    []
  )

  const operationalLinkLocked =
    form.payableId != null && form.payableId > 0 ? payable == null || !payable.canSave || payable.isSent : false

  const linkFrozenRef = useRef(operationalLinkLocked)
  linkFrozenRef.current = operationalLinkLocked

  const attachExistingOperational = useCallback((operationalEventId: number): boolean => {
    if (linkFrozenRef.current) return false
    if (formRef.current.operationalEventIds.includes(operationalEventId)) return true
    setPreviewResult(null)
    const next = produce(formRef.current, (draft) => {
      draft.operationalEventIds.push(operationalEventId)
    })
    formRef.current = next
    setForm(next)
    const nextDirty = produce(dirtyRef.current, (draft) => {
      markPayableFormDirtyPaths(draft, 'operationalEventIds')
    })
    dirtyRef.current = nextDirty
    setDirty(nextDirty)
    return true
  }, [])

  const setItems = useCallback((items: PaymentItemWire[]): boolean => {
    if (linkFrozenRef.current) return false
    setPreviewResult(null)
    const next = produce(formRef.current, (draft) => {
      draft.items = items
    })
    formRef.current = next
    setForm(next)
    const nextDirty = produce(dirtyRef.current, (draft) => {
      markPayableFormDirtyPaths(draft, 'items')
    })
    dirtyRef.current = nextDirty
    setDirty(nextDirty)
    return true
  }, [])

  const removeOperational = useCallback((operationalEventId: number): boolean => {
    if (linkFrozenRef.current) return false
    if (formRef.current.operationalEventIds.length <= 1 && formRef.current.items.length === 0) return false
    if (!formRef.current.operationalEventIds.includes(operationalEventId)) return false
    setPreviewResult(null)
    const next = produce(formRef.current, (draft) => {
      draft.operationalEventIds = draft.operationalEventIds.filter((id) => id !== operationalEventId)
    })
    formRef.current = next
    setForm(next)
    const nextDirty = produce(dirtyRef.current, (draft) => {
      markPayableFormDirtyPaths(draft, 'operationalEventIds')
    })
    dirtyRef.current = nextDirty
    setDirty(nextDirty)
    return true
  }, [])

  const commitOpts = useCallback(
    () => ({
      payableId: formRef.current.payableId ?? undefined,
      dirty: dirtyRef.current
    }),
    []
  )

  const runPreviewForReview = useCallback(async () => {
    if (!traderId) return
    if (readOnly) {
      ++previewRequestIdRef.current
      if (payable) setPreviewResult(payable)
      return
    }
    const requestId = ++previewRequestIdRef.current
    setFormError(null)
    try {
      const input = formValuesToCommitInput(formRef.current, traderId, {
        ...commitOpts(),
        publishAction: formRef.current.publishAction
      })
      const result = await previewPayableCommit(input)
      if (requestId !== previewRequestIdRef.current) return
      setPreviewResult(result)
    } catch (err: unknown) {
      if (requestId !== previewRequestIdRef.current) return
      setPreviewResult(null)
      const message = isError(err) ? err.message : 'Preview failed. Please check your entries.'
      setFormError(message)
      toast({ title: 'Preview failed', description: message, variant: 'destructive' })
    }
  }, [commitOpts, payable, previewPayableCommit, readOnly, traderId])

  const navigateToPayables = useCallback(() => {
    if (traderIdParam) {
      navigate(buildNavPathTrader(traderIdParam, ROUTE_PAYABLES))
    }
  }, [navigate, traderIdParam])

  const adoptSavedPayable = useCallback((saved: Payable) => {
    const nextForm = payableToFormValues(saved)
    formRef.current = nextForm
    dirtyRef.current = {}
    setForm(nextForm)
    setDirty({})
    setPreviewResult(saved)
  }, [])

  const attachPendingReceipts = useCallback(
    async (saved: Payable) => {
      const current = formRef.current
      const paymentId = saved.id ?? 0
      if (paymentId < 1 || current.payment == null) throw new Error(DEFERRED_RECEIPT_FAILURE)

      for (const file of pendingReceiptsRef.current) {
        await uploadDocument(traderId, file, { fileOn: { kind: 'payment', id: paymentId } }).catch((cause: unknown) => {
          throw new Error(DEFERRED_RECEIPT_FAILURE, { cause })
        })
      }

      pendingReceiptsRef.current = []
      setPendingReceipts([])
    },
    [traderId]
  )

  const handleCommit = useCallback(
    async (
      buildInput: (
        form: PayableForm,
        traderId: number,
        opts: { payableId?: number; dirty?: PayableFormDirty }
      ) => ReturnType<typeof formValuesToCommitInput>,
      opts?: { navigateAway?: boolean }
    ) => {
      if (!traderId) return
      setFormError(null)
      try {
        if (pendingReceiptsRef.current.length > 0) {
          const draft = await commitPayableCommit(commitInputForSave(formRef.current, traderId, commitOpts()))
          adoptSavedPayable(draft)
          await attachPendingReceipts(draft)
        }

        const result = await commitPayableCommit(buildInput(formRef.current, traderId, commitOpts()))
        if (opts?.navigateAway !== false) {
          dirtyRef.current = {}
          setDirty({})
          navigateToPayables()
        }
        return result
      } catch (err: unknown) {
        const validationErrorMap = wireValidationErrorMapFromTrpcError(err)
        let message: string
        if (isError(err)) {
          message = err.message
        } else if (validationErrorMap) {
          message = 'Validation failed. Please review the form.'
        } else {
          message = 'Failed to save payable.'
        }
        setFormError(message)
        toast({ title: 'Action failed', description: message, variant: 'destructive' })
        return null
      }
    },
    [adoptSavedPayable, attachPendingReceipts, commitOpts, commitPayableCommit, navigateToPayables, traderId]
  )

  const handleSave = useCallback(async () => {
    const result = await handleCommit(commitInputForSave, { navigateAway: false })
    if (!result) return

    const nextForm = payableToFormValues(result)
    formRef.current = nextForm
    dirtyRef.current = {}
    setPreviewResult(result)
    setDirty({})
    setForm(nextForm)
    toast({ title: 'Draft saved', description: 'You can now schedule or complete the payment.' })
  }, [handleCommit])

  const handlePublish = useCallback(() => {
    return handleCommit(commitInputForPublish)
  }, [handleCommit])

  const handleComplete = useCallback(async () => {
    const result = await handleCommit(commitInputForComplete)
    if (result) {
      toast(PAY_NOW_SENDING_TOAST)
    }
    return result
  }, [handleCommit])

  const handleMarkPaid = useCallback(
    async (values: { paymentDate: string; externalReference?: string; notes?: string }) => {
      if (!traderId) return null
      setFormError(null)
      try {
        const hasChangesToSave =
          !readOnly && (hasPayableFormChanges(dirtyRef.current) || pendingReceiptsRef.current.length > 0)
        const saved =
          payable != null && !hasChangesToSave
            ? payable
            : await handleCommit(commitInputForSave, { navigateAway: false })
        if (!saved?.id) return null

        if (saved !== payable) {
          const nextForm = payableToFormValues(saved)
          formRef.current = nextForm
          dirtyRef.current = {}
          setPreviewResult(saved)
          setDirty({})
          setForm(nextForm)
        }

        await markPaid({
          id: saved.id,
          orderId: saved.orderId,
          traderId,
          provider: 'manual',
          paymentDate: values.paymentDate,
          externalReference: values.externalReference,
          notes: values.notes
        })
        toast({ title: 'Marked as paid', description: 'The payable was recorded as paid outside of Klear.' })
        navigateToPayables()
        return saved
      } catch (err: unknown) {
        const message = isError(err) ? err.message : 'Failed to mark payable as paid.'
        setFormError(message)
        toast({ title: 'Action failed', description: message, variant: 'destructive' })
        return null
      }
    },
    [handleCommit, markPaid, navigateToPayables, payable, readOnly, traderId]
  )

  const locationRef = useRef(location)
  locationRef.current = location

  const syncUrl = useCallback(
    (step: string) => {
      if (!traderIdParam || readOnly) return
      let path = buildNavPathTrader(traderIdParam, ROUTE_PAYABLE)
      if (mode === 'edit' && form.payableId != null && form.payableId > 0) {
        path += `/${form.payableId}/edit`
      } else if (form.payableId != null && form.payableId > 0) {
        path += `/${form.payableId}`
      } else if (form.orderId != null && form.orderId > 0) {
        path += `/order/${form.orderId}`
      }
      const params = new URLSearchParams()
      if (form.supplierId != null && form.supplierId > 0) {
        params.set('supplierId', toString(form.supplierId))
      }
      params.set('currency', form.currency)
      const itemsParam = lineItemsToQuery(form.items)
      if (itemsParam != null) {
        params.set('items', itemsParam)
      }
      if (form.purchaseOrderId != null && form.purchaseOrderId > 0) {
        params.set('purchaseOrderId', toString(form.purchaseOrderId))
      }
      if (form.operationalEventIds.length > 0) {
        params.set('operationalIds', form.operationalEventIds.join(','))
      }
      const qs = params.toString()
      const query = qs ? `?${qs}` : ''
      const hash = step !== STEP_ID.SUPPLIER ? `#${encodeURIComponent(step)}` : ''
      const next = `${path}${query}${hash}`
      const loc = locationRef.current
      const current = `${loc.pathname}${loc.search}${loc.hash}`
      if (next === current) return
      navigate(next, { replace: true })
    },
    [
      form.supplierId,
      form.currency,
      form.items,
      form.operationalEventIds,
      form.orderId,
      form.payableId,
      form.purchaseOrderId,
      mode,
      navigate,
      readOnly,
      traderIdParam
    ]
  )

  return {
    mode,
    readOnly,
    form,
    getForm,
    dirty,
    patchForm,
    operationalLinkLocked,
    attachExistingOperational,
    removeOperational,
    setItems,
    previewResult,
    formError,
    isLoadingPayable,
    pendingReceipts,
    addPendingReceipts,
    removePendingReceipt,
    handlePublish,
    handleComplete,
    handleMarkPaid,
    handleSave,
    isPreviewing,
    isCommitting,
    hashStepSeed,
    navigateToPayables,
    syncUrl,
    runPreviewForReview,
    payable
  }
}

export type PayableWizardApi = ReturnType<typeof usePayableWizard>
